// bm-design-system: file input primitive
//
// A native <input type="file"> is unstyleable and renders an OS button that
// ignores the design tokens, so this wraps one in a labelled drop area instead:
// the input itself is visually hidden but still the real, keyboard-reachable
// control, and the <label> around it makes the whole area clickable.
//
// Controlled on a File | null, which is what Inertia's useForm wants in `data`
// (a File in the form data switches the request to multipart automatically).
import * as React from "react";
import { Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";

export interface FileInputProps {
  id: string;
  /** Mirrors the native attribute, e.g. ".html,text/html". */
  accept?: string;
  value: File | null;
  onFileChange: (file: File | null) => void;
  disabled?: boolean;
  invalid?: boolean;
  /** Shown inside the empty state under the prompt. */
  hint?: string;
  className?: string;
  /** Labels the control for assistive tech when no visible <label> is bound. */
  "aria-describedby"?: string;
}

export function formatFileSize(bytes: number) {
  if (bytes < 1024) return `${bytes} B`;
  const units = ["KB", "MB", "GB"];
  let size = bytes / 1024;
  let unit = 0;
  while (size >= 1024 && unit < units.length - 1) {
    size /= 1024;
    unit += 1;
  }
  return `${size < 10 ? size.toFixed(1) : Math.round(size)} ${units[unit]}`;
}

const FileInput = React.forwardRef<HTMLInputElement, FileInputProps>(
  (
    {
      id,
      accept,
      value,
      onFileChange,
      disabled = false,
      invalid = false,
      hint,
      className,
      ...props
    },
    forwardedRef,
  ) => {
    const innerRef = React.useRef<HTMLInputElement>(null);
    React.useImperativeHandle(forwardedRef, () => innerRef.current!, []);
    const [dragging, setDragging] = React.useState(false);

    const accepted = (file: File | null) => {
      if (disabled) return;
      onFileChange(file);
    };

    const clear = () => {
      // The native input keeps its own value, so re-picking the same file after
      // a clear would fire no change event unless we reset it too.
      if (innerRef.current) innerRef.current.value = "";
      accepted(null);
    };

    const handleDrop = (event: React.DragEvent) => {
      event.preventDefault();
      setDragging(false);
      const file = event.dataTransfer.files?.[0];
      if (file) accepted(file);
    };

    return (
      <div className={className}>
        <label
          htmlFor={id}
          onDragOver={(e) => {
            e.preventDefault();
            if (!disabled) setDragging(true);
          }}
          onDragLeave={() => setDragging(false)}
          onDrop={handleDrop}
          className={cn(
            "flex cursor-pointer flex-col items-center justify-center gap-2 rounded-md border border-dashed border-hairline bg-surface px-4 py-8 text-center font-normal transition-colors",
            !disabled && "hover:border-accent hover:bg-accent-faded",
            dragging && "border-accent bg-accent-faded",
            invalid && "border-danger",
            disabled && "cursor-not-allowed opacity-60",
          )}
        >
          <Upload aria-hidden="true" className="h-5 w-5 text-ink-muted" />
          <span className="text-sm text-ink-body">
            <span className="text-accent-display">Choose a file</span> or drag it
            here
          </span>
          {hint && <span className="text-xs text-ink-muted">{hint}</span>}
          <input
            ref={innerRef}
            id={id}
            type="file"
            accept={accept}
            disabled={disabled}
            aria-invalid={invalid || undefined}
            className="sr-only"
            onChange={(e) => accepted(e.target.files?.[0] ?? null)}
            {...props}
          />
        </label>

        {value && (
          <div className="mt-2 flex items-center justify-between gap-3 rounded-md border border-hairline bg-page px-3 py-2">
            <span className="min-w-0 truncate text-sm text-ink-display">
              {value.name}
            </span>
            <span className="flex shrink-0 items-center gap-2">
              <span className="text-xs tabular-nums text-ink-muted">
                {formatFileSize(value.size)}
              </span>
              <button
                type="button"
                onClick={clear}
                disabled={disabled}
                aria-label={`Remove ${value.name}`}
                className="inline-flex h-6 w-6 cursor-pointer items-center justify-center rounded text-ink-muted hover:text-ink-display"
              >
                <X aria-hidden="true" className="h-4 w-4" />
              </button>
            </span>
          </div>
        )}
      </div>
    );
  },
);
FileInput.displayName = "FileInput";

export { FileInput };
